import React, { Component } from 'react';
import { connect } from 'react-redux';
import Router, { withRouter } from 'next/router';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { checkAuthStrategyLoginRedirect } from 'lib/auth';
import { ErrorPage } from 'components/error';
import { whoAmI } from './actions';

export const PUBLIC = 'PUBLIC';

const isServer = typeof window === 'undefined';

const getRedirectUrl = (url) => {
  let redirectUrl = url === '/' ? '/login' : `/login?next=${url}`;
  if (url.indexOf('/courses/') === 0) {
    const parts = url.split('/');
    redirectUrl = `/login/courses/${parts[2]}`;
    if (parts.length > 3) redirectUrl += `?next=${url}`;
  } else if (url.indexOf('/paths/') === 0) {
    const parts = url.split('/');
    redirectUrl = `/login/paths/${parts[2]}`;
    if (parts.length > 3) redirectUrl += `?next=${url}`;
  }
  return redirectUrl;
};

const redirectToLogin = (context) => {
  const { asPath, reduxStore, req, res } = context;

  const next = isServer ? req.url : asPath;

  const redirect = checkAuthStrategyLoginRedirect({ next, reduxStore, res });
  if (redirect) return;

  if (isServer) {
    res.writeHead(302, { Location: getRedirectUrl(next) });
    res.end();
  } else {
    Router.push(getRedirectUrl(next));
  }
};

const redirectToTwoFactorSetup = (context) => {
  const { asPath, req, res } = context;
  if (isServer) {
    res.writeHead(302, { Location: `/setup-two-factor?next=${req.url}` });
    res.end();
  } else {
    Router.push(`/setup-two-factor?next=${asPath}`);
  }
};

const redirectToPasswordChange = (context, user) => {
  const { res } = context;
  if (isServer) {
    res.writeHead(302, {
      Location: `/set-password?key=${user.resetPasswordKey}`
    });
    res.end();
  } else {
    Router.push(`/set-password?key=${user.resetPasswordKey}`);
  }
};

const userHasRole = (user, roles) => {
  const userRoles = user.roles || [];
  let userHasRole = true;
  // go here only if we have specific roles requirements
  if (roles.length > 0) {
    userHasRole = false;
    // will allow if user has at least one role
    for (let i = 0, l = roles.length; i < l; i++) {
      if (userRoles.includes(roles[i])) {
        userHasRole = true;
        break;
      }
    }
  }
  return userHasRole;
};

const userHasPermission = (user, permissions) => {
  if (permissions.length === 0) return true;

  // superadmins have all permissions
  if (user.roles.includes('superadmin')) return true;

  const userPerms = user.permissions || [];
  for (let i = 0, l = permissions.length; i < l; i++) {
    if (userPerms.includes(permissions[i])) return true;
  }

  return false;
};

/**
 * Higher order component for Next.js `pages` components.
 *
 * Example:
 *
 * ```
 * withAuth(['PUBLIC'])(MyPage)
 * ```
 *
 * It reads the user from the redux store or calls whoami API to verify current logged in user.
 *
 * To make a page public you have to pass PUBLIC as an element of the `roles` parameter.
 * This is required to be able to show current logged in user from the first server render.
 *
 * @param roles: array of roles required to render this page. Use PUBLIC to make the page public.
 * @param permissions: array of permissions required to render this page, in addition to any role requirements.
 * @returns function(Child) React component to be wrapped. Must be a `page` component.
 */
const withAuth =
  (roles = [], permissions = []) =>
  (Child) =>
    withRouter(
      connect((state) => state)(
        class extends Component {
          static async getInitialProps(context) {
            // public page passes the role `PUBLIC` to this function
            const isPublicPage = roles.indexOf(PUBLIC) !== -1;
            const { asPath, reduxStore, req } = context;
            let user = null;

            if (isServer) {
              // happens on page first load
              const { cookie } = req.headers;
              user = await reduxStore.dispatch(whoAmI(cookie));
            } else {
              // happens on client side navigation
              user = reduxStore.getState().user;
            }

            if (
              user &&
              (user.roles.includes('user') || user.roles.includes('guest'))
            ) {
              const { serverConfig } = reduxStore.getState();
              const path = isServer ? req.url : asPath;

              const userRequiresShared2FA =
                serverConfig?.authSharedTwoFactor?.isEnabled &&
                serverConfig?.authSharedTwoFactor?.isConfigured &&
                (!serverConfig?.authSharedTwoFactor?.conditions?.length ||
                  attributesConditionsMatch({
                    conditions: serverConfig?.authSharedTwoFactor?.conditions,
                    customAttributeValues: user.customAttributes,
                    omitCustomAttributes: false,
                    user
                  }));

              const userRequires2FA =
                userRequiresShared2FA || user.twoFactorAuthIsEnabled;

              // check if two factor authentication login is required
              if (
                userRequires2FA &&
                path.indexOf('/login') !== 0 &&
                user.authenticatorType === 'local'
              ) {
                redirectToLogin(context);
              }

              // check if two factor authentication setup is required
              if (
                !user.twoFactorAuthIsEnabled &&
                path.indexOf('/setup-two-factor') !== 0 &&
                path.indexOf('/set-password') !== 0 &&
                user.authenticatorType === 'local' &&
                user.id !== 1 && // skip 2FA setup for vuoadmin
                ((user.roles.includes('admin') &&
                  serverConfig?.authRequireTwoFactor === 'admins') ||
                  (user.roles.includes('user') &&
                    serverConfig?.authRequireTwoFactor === 'all')) &&
                attributesConditionsMatch({
                  conditions:
                    serverConfig?.authRequireTwoFactorConditions?.conditions,
                  customAttributeValues: user.customAttributes,
                  user
                })
              ) {
                redirectToTwoFactorSetup(context);
              }

              // check if mandatory password change is required
              if (
                user.passwordForceChange &&
                path.indexOf('/login') !== 0 &&
                path.indexOf('/set-password') !== 0 &&
                path.indexOf('/reset-password') !== 0
              ) {
                redirectToPasswordChange(context, user);
              }

              if (!isPublicPage) {
                // verify role
                if (!userHasRole(user, roles)) return { statusCode: 404 };
                else if (user.locked) return { statusCode: 403 };
                else if (
                  user.expirationDate &&
                  Date.parse(user.expirationDate) < Date.now()
                ) {
                  return { statusCode: 403 };
                }

                // verify permissions
                if (!userHasPermission(user, permissions)) {
                  return { statusCode: 404 };
                }
              }
            } else {
              // anonymous user
              if (!isPublicPage) redirectToLogin(context);
            }

            if (typeof Child.getInitialProps === 'function') {
              try {
                const initProps = await Child.getInitialProps(context);
                return initProps;
              } catch (err) {
                const statusCode =
                  err && err.response && err.response.status
                    ? err.response.status
                    : 500;
                return { statusCode };
              }
            }

            return {};
          }

          render() {
            if (this.props.statusCode) return <ErrorPage {...this.props} />;
            return <Child {...this.props} />;
          }
        }
      )
    );

export default withAuth;
